import React, { useState, useEffect, useRef } from 'react';
import { useTabCompletion } from '../../hooks/useTabCompletion';
import { GhostInput } from './GhostInput';
import { useStore } from '../../store/useStore';
import type { Resource } from '../../types';

interface LogMessage {
    id: number;
    text: string;
    type: 'info' | 'error' | 'success' | 'command';
}

export const DebugConsole: React.FC = () => {
    const commands = [
        '/help',
        '/set_gold',
        '/set_skill',
        '/add_resource',
        '/unlock_upgrades',
        '/clear'
    ];
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const { handleKeyDown: handleTabKeyDown, handleInputChange, hint } = useTabCompletion(input, setInput, commands);
    const [logs, setLogs] = useState<LogMessage[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const setResource = useStore(state => state.setResource);
    const setSkillLevel = useStore(state => state.setSkillLevel);
    const unlockAllUpgrades = useStore(state => state.unlockAllUpgrades);

    const addLog = (text: string, type: LogMessage['type'] = 'info') => {
        setLogs(prev => [...prev, { id: Date.now(), text, type }]);
    };

    const handleCommand = (cmdStr: string) => {
        const parts = cmdStr.trim().split(' ');
        // Default to empty string if no command
        const command = (parts[0] || '').toLowerCase();
        const args = parts.slice(1);

        addLog(`> ${cmdStr}`, 'command');

        switch (command) {
            case '/help':
                addLog('Available commands:', 'info');
                addLog('/set_gold <amount> - Set your gold amount', 'info');
                addLog('/set_skill <name> <level> - Set a skill level (mining, farming, crafting)', 'info');
                addLog('/add_resource <name> <amount> - Add any resource', 'info');
                addLog('/unlock_upgrades - Unlock all upgrades', 'info');
                addLog('/clear - Clear console', 'info');
                break;

            case '/clear':
                setLogs([]);
                break;

            case '/set_gold': {
                const amountStr = args[0];
                if (!amountStr) {
                    addLog('Usage: /set_gold <amount>', 'error');
                    return;
                }
                const amount = parseInt(amountStr, 10);
                if (isNaN(amount)) {
                    addLog('Invalid amount', 'error');
                    return;
                }
                setResource('gold', amount);
                addLog(`Set gold to ${amount}`, 'success');
                break;
            }

            case '/set_skill': {
                const skillName = args[0]?.toLowerCase();
                const levelStr = args[1];

                if (!skillName || !levelStr) {
                    addLog('Usage: /set_skill <name> <level>', 'error');
                    return;
                }

                const level = parseInt(levelStr, 10);
                if (isNaN(level)) {
                    addLog('Invalid level', 'error');
                    return;
                }

                const validSkills = ['mining', 'farming', 'crafting'];
                if (!validSkills.includes(skillName)) {
                    addLog(`Invalid skill. Available: ${validSkills.join(', ')}`, 'error');
                    return;
                }

                setSkillLevel(skillName, level);
                addLog(`Set ${skillName} level to ${level}`, 'success');
                break;
            }

            case '/add_resource': {
                const resName = args[0] as Resource;
                const amountStr = args[1];

                if (!resName || !amountStr) {
                    addLog('Usage: /add_resource <name> <amount>', 'error');
                    return;
                }

                const count = parseInt(amountStr, 10);
                if (isNaN(count)) {
                    addLog('Invalid amount', 'error');
                    return;
                }

                // Use getState to access actions not bound in component scope if needed, 
                // but here we can just use the store directly if we wanted. 
                // However, addResource wasn't destructured above, so we use getState().
                useStore.getState().addResource(resName, count);
                addLog(`Added ${count} ${resName}`, 'success');
                break;
            }

            case '/unlock_upgrades':
                unlockAllUpgrades();
                addLog('All upgrades unlocked!', 'success');
                break;

            default:
                addLog(`Unknown command: ${command}. Type /help for list.`, 'error');
        }
    };

    // Toggle Listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Toggle on ` or ~ (backtick)
            if (e.key === '`' || e.key === '~') {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [logs, isOpen]);

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '50vh', // Half screen
            backgroundColor: 'rgba(0, 0, 0, 0.90)',
            backdropFilter: 'blur(4px)',
            borderTop: '2px solid #333',
            color: '#fff',
            fontFamily: 'monospace',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            boxSizing: 'border-box'
        }}>
            <div style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace' // Better font stack
            }} ref={scrollRef}>
                {logs.map(log => (
                    <div key={log.id} style={{
                        color: log.type === 'error' ? '#ff6b6b' :
                            log.type === 'success' ? '#51cf66' :
                                log.type === 'command' ? '#ffd43b' : '#e9ecef'
                    }}>
                        {log.text}
                    </div>
                ))}
            </div>
            <form onSubmit={(e) => {
                e.preventDefault();
                if (input.trim()) {
                    handleCommand(input);
                    setInput('');
                }
            }} style={{ display: 'flex', gap: '8px', borderTop: '1px solid #444', paddingTop: '10px' }}>
                <span style={{ color: '#ffd43b', fontWeight: 'bold' }}>{'>'}</span>
                <span style={{ color: '#ffd43b', fontWeight: 'bold' }}>{'>'}</span>
                <GhostInput
                    ref={inputRef}
                    value={input}
                    hint={hint}
                    onChange={handleInputChange}
                    onKeyDown={handleTabKeyDown}
                    style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                        fontSize: '14px',
                        outline: 'none'
                    }}
                    placeholder="Type /help for cheat commands..."
                />
            </form>
        </div>
    );
};

